import { supabase } from '../lib/supabase';

// ============================================
// EMAIL ALERT SERVICE
// ============================================

// Profile Views
export const profileViewService = {
  async trackView(playerId, viewData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get viewer profile
    const { data: viewerProfile } = await supabase
      ?.from('user_profiles')
      ?.select('role, organization')
      ?.eq('id', user?.id)
      ?.single();
    
    const snakeCaseData = {
      player_id: playerId,
      viewer_id: user?.id,
      viewer_role: viewerProfile?.role,
      viewer_organization: viewerProfile?.organization,
      view_duration: viewData?.viewDuration || 0,
      viewed_sections: viewData?.viewedSections || []
    };
    
    const { data, error } = await supabase
      ?.from('profile_views')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async getViewsByPlayer(playerId) {
    const { data, error } = await supabase
      ?.from('profile_views')
      ?.select('*, viewer:user_profiles!profile_views_viewer_id_fkey(full_name, organization, role)')
      ?.eq('player_id', playerId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getViewStats(playerId) {
    const { data, error } = await supabase
      ?.from('profile_views')
      ?.select('viewer_role, created_at')
      ?.eq('player_id', playerId);
    
    if (error) throw error;
    
    // Calculate stats
    const totalViews = data?.length || 0;
    const scoutViews = data?.filter(v => v?.viewer_role === 'scout')?.length || 0;
    const clubViews = data?.filter(v => v?.viewer_role === 'club')?.length || 0;
    
    // Views in last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
    const recentViews = data?.filter(v => new Date(v?.created_at) > sevenDaysAgo)?.length || 0;
    
    return {
      data: {
        totalViews,
        scoutViews,
        clubViews,
        recentViews
      }
    };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      viewerId: obj?.viewer_id,
      viewer: obj?.viewer,
      viewerRole: obj?.viewer_role,
      viewerOrganization: obj?.viewer_organization,
      viewDuration: obj?.view_duration,
      viewedSections: obj?.viewed_sections,
      createdAt: obj?.created_at
    };
  }
};

// Club Interest
export const clubInterestService = {
  async create(interestData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const snakeCaseData = {
      player_id: interestData?.playerId,
      club_id: user?.id,
      interest_level: interestData?.interestLevel || 'monitoring',
      notes: interestData?.notes,
      priority: interestData?.priority || 'normal',
      is_active: true
    };
    
    const { data, error } = await supabase
      ?.from('club_interest')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async update(id, interestData) {
    const snakeCaseData = {
      interest_level: interestData?.interestLevel,
      notes: interestData?.notes,
      priority: interestData?.priority,
      is_active: interestData?.isActive,
      updated_at: new Date()?.toISOString()
    };
    
    const { data, error } = await supabase
      ?.from('club_interest')
      ?.update(snakeCaseData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async getByPlayer(playerId) {
    const { data, error } = await supabase
      ?.from('club_interest')
      ?.select('*, club:user_profiles!club_interest_club_id_fkey(full_name, organization)')
      ?.eq('player_id', playerId)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getByClub(clubId) {
    const { data, error } = await supabase
      ?.from('club_interest')
      ?.select('*, player:player_master_profiles(full_name, primary_position, current_market_value, profile_photo)')
      ?.eq('club_id', clubId)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      clubId: obj?.club_id,
      club: obj?.club,
      player: obj?.player ? {
        fullName: obj?.player?.full_name,
        primaryPosition: obj?.player?.primary_position,
        currentMarketValue: obj?.player?.current_market_value,
        profilePhoto: obj?.player?.profile_photo
      } : null,
      interestLevel: obj?.interest_level,
      notes: obj?.notes,
      priority: obj?.priority,
      isActive: obj?.is_active,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// Notification Preferences
export const notificationPreferencesService = {
  async get() {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.single();
    
    if (error && error?.code !== 'PGRST116') {
      throw error;
    }
    
    // Return default preferences if none exist
    if (!data) {
      return {
        data: {
          emailNotifications: true,
          smsNotifications: false,
          pushNotifications: true,
          profileViewAlerts: true,
          clubInterestAlerts: true,
          transferInquiryAlerts: true,
          marketOpportunityAlerts: true,
          weeklyDigest: false,
          monthlyReport: true
        }
      };
    }
    
    return { data: this.toCamelCase(data) };
  },

  async update(preferences) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const snakeCaseData = {
      user_id: user?.id,
      email_notifications: preferences?.emailNotifications,
      sms_notifications: preferences?.smsNotifications,
      push_notifications: preferences?.pushNotifications,
      profile_view_alerts: preferences?.profileViewAlerts,
      club_interest_alerts: preferences?.clubInterestAlerts,
      transfer_inquiry_alerts: preferences?.transferInquiryAlerts,
      market_opportunity_alerts: preferences?.marketOpportunityAlerts,
      weekly_digest: preferences?.weeklyDigest,
      monthly_report: preferences?.monthlyReport,
      updated_at: new Date()?.toISOString()
    };
    
    const { data, error } = await supabase
      ?.from('notification_preferences')
      ?.upsert([snakeCaseData], { onConflict: 'user_id' })
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      userId: obj?.user_id,
      emailNotifications: obj?.email_notifications,
      smsNotifications: obj?.sms_notifications,
      pushNotifications: obj?.push_notifications,
      profileViewAlerts: obj?.profile_view_alerts,
      clubInterestAlerts: obj?.club_interest_alerts,
      transferInquiryAlerts: obj?.transfer_inquiry_alerts,
      marketOpportunityAlerts: obj?.market_opportunity_alerts,
      weeklyDigest: obj?.weekly_digest,
      monthlyReport: obj?.monthly_report,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// Email Queue
export const emailQueueService = {
  async getQueue() {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      ?.from('email_queue')
      ?.select('*')
      ?.eq('recipient_id', user?.id)
      ?.order('created_at', { ascending: false })
      ?.limit(50);
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async triggerEmailProcessing() {
    const { data, error } = await supabase?.functions?.invoke('send-event-alerts');
    
    if (error) throw error;
    return { data };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      recipientId: obj?.recipient_id,
      recipientEmail: obj?.recipient_email,
      eventType: obj?.event_type,
      subject: obj?.subject,
      htmlContent: obj?.html_content,
      metadata: obj?.metadata,
      sent: obj?.sent,
      sentAt: obj?.sent_at,
      errorMessage: obj?.error_message,
      createdAt: obj?.created_at
    };
  }
};

// Real-time subscriptions for email alerts
export const subscribeToProfileViews = (playerId, callback) => {
  const channel = supabase
    ?.channel('profile_views_alerts')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'profile_views',
        filter: `player_id=eq.${playerId}`
      },
      (payload) => {
        console.log('New profile view:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

export const subscribeToClubInterest = (playerId, callback) => {
  const channel = supabase
    ?.channel('club_interest_alerts')
    ?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'club_interest',
        filter: `player_id=eq.${playerId}`
      },
      (payload) => {
        console.log('New club interest:', payload);
        callback(payload);
      }
    )
    ?.subscribe();
  
  return channel;
};

// Scout Feedback Service
export const scoutFeedbackService = {
  async create(feedbackData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get scout profile
    const { data: scoutProfile } = await supabase
      ?.from('user_profiles')
      ?.select('full_name, organization')
      ?.eq('id', user?.id)
      ?.single();
    
    const snakeCaseData = {
      player_id: feedbackData?.playerId,
      scout_id: user?.id,
      scout_name: scoutProfile?.full_name,
      scout_organization: scoutProfile?.organization,
      rating: feedbackData?.rating,
      technical_rating: feedbackData?.technicalRating,
      physical_rating: feedbackData?.physicalRating,
      mental_rating: feedbackData?.mentalRating,
      potential_rating: feedbackData?.potentialRating,
      strengths: feedbackData?.strengths,
      weaknesses: feedbackData?.weaknesses,
      summary: feedbackData?.summary,
      recommended_level: feedbackData?.recommendedLevel,
      is_anonymous: feedbackData?.isAnonymous || false,
      is_visible_to_player: feedbackData?.isVisibleToPlayer !== false
    };
    
    const { data, error } = await supabase
      ?.from('scout_feedback')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async getByPlayer(playerId) {
    const { data, error } = await supabase
      ?.from('scout_feedback')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.eq('is_visible_to_player', true)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getSummary(playerId) {
    const { data, error } = await supabase
      ?.from('scout_feedback')
      ?.select('rating, technical_rating, physical_rating, mental_rating, potential_rating, strengths, weaknesses')
      ?.eq('player_id', playerId)
      ?.eq('is_visible_to_player', true);
    
    if (error) throw error;
    
    if (!data || data?.length === 0) {
      return {
        data: {
          totalFeedback: 0,
          avgRating: 0,
          avgTechnical: 0,
          avgPhysical: 0,
          avgMental: 0,
          avgPotential: 0,
          topStrengths: [],
          topWeaknesses: []
        }
      };
    }
    
    // Calculate averages
    const totalFeedback = data?.length;
    const avgRating = data?.reduce((sum, f) => sum + (f?.rating || 0), 0) / totalFeedback;
    const avgTechnical = data?.reduce((sum, f) => sum + (f?.technical_rating || 0), 0) / totalFeedback;
    const avgPhysical = data?.reduce((sum, f) => sum + (f?.physical_rating || 0), 0) / totalFeedback;
    const avgMental = data?.reduce((sum, f) => sum + (f?.mental_rating || 0), 0) / totalFeedback;
    const avgPotential = data?.reduce((sum, f) => sum + (f?.potential_rating || 0), 0) / totalFeedback;
    
    // Aggregate strengths and weaknesses
    const strengthsMap = {};
    const weaknessesMap = {};
    
    data?.forEach(feedback => {
      feedback?.strengths?.forEach(s => {
        strengthsMap[s] = (strengthsMap?.[s] || 0) + 1;
      });
      feedback?.weaknesses?.forEach(w => {
        weaknessesMap[w] = (weaknessesMap?.[w] || 0) + 1;
      });
    });
    
    const topStrengths = Object.entries(strengthsMap)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 5)
      ?.map(([strength, count]) => ({ strength, count }));
    
    const topWeaknesses = Object.entries(weaknessesMap)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 5)
      ?.map(([weakness, count]) => ({ weakness, count }));
    
    return {
      data: {
        totalFeedback,
        avgRating: avgRating?.toFixed(1),
        avgTechnical: avgTechnical?.toFixed(1),
        avgPhysical: avgPhysical?.toFixed(1),
        avgMental: avgMental?.toFixed(1),
        avgPotential: avgPotential?.toFixed(1),
        topStrengths,
        topWeaknesses
      }
    };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      scoutId: obj?.scout_id,
      scoutName: obj?.scout_name,
      scoutOrganization: obj?.scout_organization,
      rating: obj?.rating,
      technicalRating: obj?.technical_rating,
      physicalRating: obj?.physical_rating,
      mentalRating: obj?.mental_rating,
      potentialRating: obj?.potential_rating,
      strengths: obj?.strengths,
      weaknesses: obj?.weaknesses,
      summary: obj?.summary,
      recommendedLevel: obj?.recommended_level,
      isAnonymous: obj?.is_anonymous,
      isVisibleToPlayer: obj?.is_visible_to_player,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// Interest Tracking Service
export const interestTrackingService = {
  async create(trackingData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get viewer profile
    const { data: viewerProfile } = await supabase
      ?.from('user_profiles')
      ?.select('role, organization')
      ?.eq('id', user?.id)
      ?.single();
    
    const snakeCaseData = {
      player_id: trackingData?.playerId,
      viewer_id: user?.id,
      viewer_role: viewerProfile?.role,
      viewer_organization: viewerProfile?.organization,
      interested_position: trackingData?.interestedPosition,
      interested_league: trackingData?.interestedLeague,
      interest_strength: trackingData?.interestStrength || 'low',
      notes: trackingData?.notes
    };
    
    const { data, error } = await supabase
      ?.from('interest_tracking')
      ?.insert([snakeCaseData])
      ?.select()
      ?.single();
    
    if (error) throw error;
    return { data: this.toCamelCase(data) };
  },

  async getByPlayer(playerId) {
    const { data, error } = await supabase
      ?.from('interest_tracking')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    return { data: data?.map(this.toCamelCase) };
  },

  async getPositionLeagueStats(playerId) {
    const { data, error } = await supabase
      ?.from('interest_tracking')
      ?.select('interested_position, interested_league, interest_strength')
      ?.eq('player_id', playerId);
    
    if (error) throw error;
    
    // Aggregate by position
    const positionMap = {};
    const leagueMap = {};
    
    data?.forEach(item => {
      if (item?.interested_position) {
        positionMap[item?.interested_position] = (positionMap?.[item?.interested_position] || 0) + 1;
      }
      if (item?.interested_league) {
        leagueMap[item?.interested_league] = (leagueMap?.[item?.interested_league] || 0) + 1;
      }
    });
    
    const topPositions = Object.entries(positionMap)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 5)
      ?.map(([position, count]) => ({ position, count }));
    
    const topLeagues = Object.entries(leagueMap)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 5)
      ?.map(([league, count]) => ({ league, count }));
    
    return {
      data: {
        topPositions,
        topLeagues,
        totalInterest: data?.length || 0
      }
    };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      viewerId: obj?.viewer_id,
      viewerRole: obj?.viewer_role,
      viewerOrganization: obj?.viewer_organization,
      interestedPosition: obj?.interested_position,
      interestedLeague: obj?.interested_league,
      interestStrength: obj?.interest_strength,
      notes: obj?.notes,
      createdAt: obj?.created_at,
      updatedAt: obj?.updated_at
    };
  }
};

// Visitor Analytics Summary Service
export const visitorAnalyticsService = {
  async getSummary(playerId) {
    const { data, error } = await supabase
      ?.from('visitor_analytics_summary')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.single();
    
    if (error && error?.code !== 'PGRST116') {
      throw error;
    }
    
    // Return default if not found
    if (!data) {
      return {
        data: {
          totalViews: 0,
          scoutViews: 0,
          clubViews: 0,
          agentViews: 0,
          totalClubInterest: 0,
          activeClubInterest: 0,
          totalScoutFeedback: 0,
          avgScoutRating: 0,
          viewsLast7Days: 0,
          viewsLast30Days: 0,
          topInterestedPositions: [],
          topInterestedLeagues: [],
          lastUpdated: new Date()?.toISOString()
        }
      };
    }
    
    return { data: this.toCamelCase(data) };
  },

  async getByCurrentUser() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Use assigned_profile_id from user_profiles — works for admin-assigned players
    // who may have null user_id on their player_master_profiles record
    const { data: userProfile } = await supabase
      ?.from('user_profiles')
      ?.select('assigned_profile_id')
      ?.eq('id', user?.id)
      ?.single();

    if (!userProfile?.assigned_profile_id) {
      throw new Error('Player profile not found');
    }

    return this.getSummary(userProfile?.assigned_profile_id);
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      totalViews: obj?.total_views,
      scoutViews: obj?.scout_views,
      clubViews: obj?.club_views,
      agentViews: obj?.agent_views,
      totalClubInterest: obj?.total_club_interest,
      activeClubInterest: obj?.active_club_interest,
      totalScoutFeedback: obj?.total_scout_feedback,
      avgScoutRating: obj?.avg_scout_rating,
      viewsLast7Days: obj?.views_last_7_days,
      viewsLast30Days: obj?.views_last_30_days,
      topInterestedPositions: obj?.top_interested_positions,
      topInterestedLeagues: obj?.top_interested_leagues,
      lastUpdated: obj?.last_updated
    };
  }
};

// Player Comparison Service
export const playerComparisonService = {
  async getComparison(playerId) {
    const { data, error } = await supabase
      ?.from('player_comparison_data')
      ?.select('*')
      ?.eq('player_id', playerId)
      ?.order('calculated_at', { ascending: false })
      ?.limit(1)
      ?.single();
    
    if (error && error?.code !== 'PGRST116') {
      throw error;
    }
    
    return { data: data ? this.toCamelCase(data) : null };
  },

  toCamelCase(obj) {
    if (!obj) return obj;
    return {
      id: obj?.id,
      playerId: obj?.player_id,
      comparisonPeriod: obj?.comparison_period,
      marketPosition: obj?.market_position,
      totalPlayersInCategory: obj?.total_players_in_category,
      percentile: obj?.percentile,
      avgProfileViews: obj?.avg_profile_views,
      avgClubInterest: obj?.avg_club_interest,
      avgScoutViews: obj?.avg_scout_views,
      positionCategory: obj?.position_category,
      ageCategory: obj?.age_category,
      leagueCategory: obj?.league_category,
      calculatedAt: obj?.calculated_at
    };
  }
};